import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import "leaflet/dist/leaflet.css";
import L from "leaflet";
import DOMPurify from "dompurify";

import {
  CircleMarker,
  MapContainer,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";

import { Link } from "react-router-dom";

import {
  FiArrowRight,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiCreditCard,
  FiExternalLink,
  FiFacebook,
  FiGlobe,
  FiInfo,
  FiInstagram,
  FiMail,
  FiMapPin,
  FiPhone,
  FiRefreshCw,
  FiTag,
  FiUser,
  FiUsers,
} from "react-icons/fi";

import {
  FaCity,
  FaStore,
  FaUsers,
} from "react-icons/fa";

import Reveal from "./ui/Reveal";

import { getAssociations } from "../services/associationsServices";
import { getAllCommerces } from "../services/commercesServices";
import { getAllServicesMunicipaux } from "../services/servicesMunicipauxServices";

/*
 * Le fichier est désormais :
 *
 * /src/utils/carteInteretsUtils.js
 */
import {
  ensureArray,
  normalizeAssociationPoint,
  normalizeCommercePoint,
  normalizeServicePoint,
  sortPoints,
} from "../utils/carteInteretsUtils";

import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  POINT_TYPES,
} from "../pages/CarteInterets/data/carteInteretsUiData";

/* -------------------------------------------------------------------------- */
/*                                  Config                                    */
/* -------------------------------------------------------------------------- */

const TYPE_ICONS = {
  association: FaUsers,
  commerce: FaStore,
  service: FaCity,
};

const DESCRIPTION_COLLAPSED_HEIGHT = 145;

/* -------------------------------------------------------------------------- */
/*                               Helpers                                      */
/* -------------------------------------------------------------------------- */

function formatHours(value) {
  if (!value) return "";

  /*
   * Cas texte classique :
   * notamment horaires_service.
   */
  if (typeof value === "string") {
    return value.trim();
  }

  /*
   * Cas tableau.
   */
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item === null || item === undefined) {
          return null;
        }

        if (typeof item === "string") {
          return item.trim();
        }

        if (typeof item === "object") {
          return Object.entries(item)
            .map(([key, itemValue]) => {
              if (
                itemValue === null ||
                itemValue === undefined ||
                itemValue === ""
              ) {
                return null;
              }

              return `${key} : ${String(itemValue)}`;
            })
            .filter(Boolean)
            .join(" — ");
        }

        return String(item);
      })
      .filter(Boolean)
      .join("\n");
  }

  /*
   * Cas JSON des commerces.
   */
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, itemValue]) => {
        if (
          itemValue === null ||
          itemValue === undefined ||
          itemValue === ""
        ) {
          return null;
        }

        if (typeof itemValue === "string") {
          return `${key} : ${itemValue}`;
        }

        if (Array.isArray(itemValue)) {
          return `${key} : ${itemValue.join(" / ")}`;
        }

        if (typeof itemValue === "object") {
          const nestedValue = Object.entries(itemValue)
            .map(([nestedKey, nestedItem]) => {
              if (
                nestedItem === null ||
                nestedItem === undefined ||
                nestedItem === ""
              ) {
                return null;
              }

              return `${nestedKey} ${nestedItem}`;
            })
            .filter(Boolean)
            .join(" / ");

          return nestedValue
            ? `${key} : ${nestedValue}`
            : null;
        }

        return `${key} : ${String(itemValue)}`;
      })
      .filter(Boolean)
      .join("\n");
  }

  return String(value);
}

/* -------------------------------------------------------------------------- */
/*                         RichTextPreview                                    */
/* -------------------------------------------------------------------------- */

function RichTextPreview({
  value,
  collapsedHeight = DESCRIPTION_COLLAPSED_HEIGHT,
  showToggle = true,
}) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  const contentRef = useRef(null);

  const safeHtml = useMemo(() => {
    const html = String(value || "").trim();

    if (!html) return "";

    return DOMPurify.sanitize(html, {
      USE_PROFILES: {
        html: true,
      },
      ADD_ATTR: [
        "target",
        "rel",
      ],
    });
  }, [value]);

  /*
   * Lorsqu'on change de contenu, on replie l'aperçu.
   */
  useEffect(() => {
    setExpanded(false);
  }, [safeHtml]);

  /*
   * Détection de la hauteur réelle du contenu.
   */
  useEffect(() => {
    const element = contentRef.current;

    if (!element || !safeHtml) {
      setOverflowing(false);
      return undefined;
    }

    const checkOverflow = () => {
      const naturalHeight =
        element.scrollHeight;

      setOverflowing(
        naturalHeight >
          collapsedHeight + 8,
      );
    };

    checkOverflow();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(
            checkOverflow,
          )
        : null;

    resizeObserver?.observe(
      element,
    );

    window.addEventListener(
      "resize",
      checkOverflow,
    );

    return () => {
      resizeObserver?.disconnect();

      window.removeEventListener(
        "resize",
        checkOverflow,
      );
    };
  }, [
    safeHtml,
    collapsedHeight,
  ]);

  if (!safeHtml) {
    return null;
  }

  return (
    <div>
      <div
        className="relative overflow-hidden transition-[max-height] duration-300 ease-out"
        style={{
          maxHeight:
            expanded ||
            !showToggle
              ? "3000px"
              : `${collapsedHeight}px`,
        }}
      >
        <div
          ref={contentRef}
          className="
            text-sm
            leading-7
            text-slate-600
            dark:text-slate-300

            [&_p]:mb-3
            [&_p:last-child]:mb-0

            [&_strong]:font-bold
            [&_strong]:text-slate-800
            dark:[&_strong]:text-slate-100

            [&_b]:font-bold
            [&_b]:text-slate-800
            dark:[&_b]:text-slate-100

            [&_em]:italic
            [&_i]:italic

            [&_u]:underline
            [&_u]:underline-offset-2

            [&_h1]:mb-3
            [&_h1]:mt-4
            [&_h1]:text-xl
            [&_h1]:font-black
            [&_h1]:leading-tight
            [&_h1]:text-slate-950
            dark:[&_h1]:text-white

            [&_h2]:mb-2
            [&_h2]:mt-4
            [&_h2]:text-lg
            [&_h2]:font-black
            [&_h2]:leading-tight
            [&_h2]:text-slate-950
            dark:[&_h2]:text-white

            [&_h3]:mb-2
            [&_h3]:mt-3
            [&_h3]:text-base
            [&_h3]:font-bold
            [&_h3]:text-slate-900
            dark:[&_h3]:text-slate-100

            [&_ul]:my-3
            [&_ul]:list-disc
            [&_ul]:space-y-1.5
            [&_ul]:pl-5

            [&_ol]:my-3
            [&_ol]:list-decimal
            [&_ol]:space-y-1.5
            [&_ol]:pl-5

            [&_li]:pl-1

            [&_blockquote]:my-3
            [&_blockquote]:rounded-r-xl
            [&_blockquote]:border-l-4
            [&_blockquote]:border-sky-300
            [&_blockquote]:bg-sky-50/70
            [&_blockquote]:px-4
            [&_blockquote]:py-2
            [&_blockquote]:italic
            [&_blockquote]:text-slate-600
            dark:[&_blockquote]:border-sky-700
            dark:[&_blockquote]:bg-sky-950/20
            dark:[&_blockquote]:text-slate-300

            [&_a]:font-bold
            [&_a]:text-[#1277B9]
            [&_a]:underline
            [&_a]:decoration-[#1277B9]/30
            [&_a]:underline-offset-2
            [&_a]:transition
            [&_a:hover]:decoration-[#1277B9]

            dark:[&_a]:text-sky-400

            [&_hr]:my-4
            [&_hr]:border-slate-200
            dark:[&_hr]:border-slate-800
          "
          dangerouslySetInnerHTML={{
            __html: safeHtml,
          }}
        />

        {!expanded &&
        showToggle &&
        overflowing ? (
          <div
            className="
              pointer-events-none
              absolute
              inset-x-0
              bottom-0
              h-20
              bg-gradient-to-t
              from-white
              via-white/95
              to-transparent
              dark:from-slate-950
              dark:via-slate-950/95
            "
          />
        ) : null}
      </div>

      {showToggle &&
      overflowing ? (
        <button
          type="button"
          onClick={() =>
            setExpanded(
              (previous) =>
                !previous,
            )
          }
          className="
            mt-2
            inline-flex
            items-center
            gap-1.5
            rounded-xl
            px-2.5
            py-1.5
            text-xs
            font-bold
            text-[#1277B9]
            transition
            hover:bg-sky-50
            dark:text-sky-300
            dark:hover:bg-sky-500/10
          "
        >
          {expanded ? (
            <>
              Réduire
              <FiChevronUp className="h-4 w-4" />
            </>
          ) : (
            <>
              Lire la suite
              <FiChevronDown className="h-4 w-4" />
            </>
          )}
        </button>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                              DetailRow                                     */
/* -------------------------------------------------------------------------- */

function DetailRow({
  icon: Icon,
  label,
  children,
}) {
  if (
    children === null ||
    children === undefined ||
    children === ""
  ) {
    return null;
  }

  return (
    <div className="flex items-start gap-3 rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-900">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#1277B9] shadow-sm dark:bg-slate-950 dark:text-sky-400">
        <Icon className="h-4 w-4" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">
          {label}
        </p>

        <div className="mt-1 break-words text-sm font-semibold leading-6 text-slate-700 dark:text-slate-200">
          {children}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                             ResizeHandler                                  */
/* -------------------------------------------------------------------------- */

function ResizeHandler() {
  const map = useMap();

  useEffect(() => {
    const timer =
      window.setTimeout(() => {
        map.invalidateSize();
      }, 120);

    return () =>
      window.clearTimeout(timer);
  }, [map]);

  return null;
}

/* -------------------------------------------------------------------------- */
/*                        MapBoundsController                                 */
/* -------------------------------------------------------------------------- */

function MapBoundsController({
  points = [],
  selectedPoint,
}) {
  const map = useMap();

  useEffect(() => {
    /*
     * Si un point est sélectionné :
     * on centre la carte dessus.
     */
    if (selectedPoint) {
      map.flyTo(
        [
          selectedPoint.lat,
          selectedPoint.lng,
        ],
        15,
        {
          duration: 0.6,
        },
      );

      return;
    }

    /*
     * Plusieurs points :
     * cadrage automatique.
     */
    if (points.length > 1) {
      const bounds =
        L.latLngBounds(
          points.map(
            (point) => [
              point.lat,
              point.lng,
            ],
          ),
        );

      map.fitBounds(bounds, {
        padding: [30, 30],
        maxZoom: 15,
      });

      return;
    }

    /*
     * Un seul point.
     */
    if (points.length === 1) {
      map.flyTo(
        [
          points[0].lat,
          points[0].lng,
        ],
        15,
        {
          duration: 0.6,
        },
      );

      return;
    }

    /*
     * Aucun point.
     */
    map.setView(
      DEFAULT_CENTER,
      DEFAULT_ZOOM,
    );
  }, [
    map,
    points,
    selectedPoint,
  ]);

  return null;
}

/* -------------------------------------------------------------------------- */
/*                             TypeStatButton                                 */
/* -------------------------------------------------------------------------- */

function TypeStatButton({
  type,
  active,
  count,
  onClick,
}) {
  const meta =
    POINT_TYPES[type];

  const Icon =
    TYPE_ICONS[type] ||
    FiMapPin;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group inline-flex min-h-11 shrink-0 items-center gap-2 rounded-2xl border px-4 text-sm font-bold shadow-sm transition hover:-translate-y-0.5 ${
        active
          ? "border-transparent text-white"
          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
      }`}
      style={
        active
          ? {
              backgroundColor:
                meta.color,
            }
          : undefined
      }
    >
      <span
        className={`flex h-7 w-7 items-center justify-center rounded-xl ${
          active
            ? "bg-white/20 text-white"
            : "bg-slate-100 dark:bg-slate-900"
        }`}
        style={
          !active
            ? {
                color:
                  meta.color,
              }
            : undefined
        }
      >
        <Icon className="h-3.5 w-3.5" />
      </span>

      <span>
        {meta.shortLabel}
      </span>

      <span
        className={`rounded-full px-2 py-0.5 text-xs ${
          active
            ? "bg-white/20 text-white"
            : "bg-slate-100 text-slate-500 dark:bg-slate-900 dark:text-slate-400"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*                          SelectedPointCard                                 */
/* -------------------------------------------------------------------------- */

function SelectedPointCard({
  point,
}) {
  const [
    showDetails,
    setShowDetails,
  ] = useState(false);

  /*
   * Quand on change de marqueur,
   * on replie automatiquement la fiche.
   */
  useEffect(() => {
    setShowDetails(false);
  }, [point?.id]);

  if (!point) {
    return (
      <div className="rounded-[1.75rem] border border-dashed border-slate-300 bg-white/80 p-5 text-center dark:border-slate-700 dark:bg-slate-950/70">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-3xl bg-[#1277B9]/10 text-[#1277B9]">
          <FiMapPin className="h-6 w-6" />
        </div>

        <p className="mt-4 text-base font-bold text-slate-950 dark:text-white">
          Aucun lieu sélectionné
        </p>

        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
          Cliquez sur un point de la carte pour afficher un aperçu.
        </p>
      </div>
    );
  }

  const type =
    POINT_TYPES[point.type] ||
    POINT_TYPES.service;

  /*
   * Détermine si nous avons quelque chose
   * à afficher dans la fiche détaillée.
   */
  const hasMoreInformation =
    Boolean(
      point.tagline ||
        point.contact ||
        point.contactRole ||
        point.phone ||
        point.secondaryPhone ||
        point.emails?.length ||
        point.email ||
        point.website ||
        point.secondaryUrl ||
        point.facebook ||
        point.instagram ||
        point.tags ||
        point.services ||
        point.accessibility ||
        point.paymentMethods ||
        point.hours ||
        point.additionalInfo ||
        point.siret ||
        point.serviceType,
    );

  const emails =
    Array.isArray(
      point.emails,
    ) &&
    point.emails.length
      ? point.emails
      : point.email
        ? [point.email]
        : [];

  const formattedHours =
    formatHours(point.hours);

  return (
    <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
      {/* -------------------------------------------------------------------- */}
      {/* Header                                                               */}
      {/* -------------------------------------------------------------------- */}

      <div className="flex items-start gap-3">
        <span
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-sm font-bold text-white shadow-sm"
          style={{
            backgroundColor:
              type.color,
          }}
        >
          {type.shortLabel.slice(
            0,
            1,
          )}
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
            {type.singular}

            {point.category
              ? ` · ${point.category}`
              : ""}
          </p>

          <h3 className="mt-1 line-clamp-2 text-xl font-bold leading-tight text-slate-950 dark:text-white">
            {point.title}
          </h3>

          {point.serviceType ? (
            <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
              {point.serviceType}
            </p>
          ) : null}
        </div>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* Accroche                                                             */}
      {/* -------------------------------------------------------------------- */}

      {point.tagline ? (
        <p className="mt-4 rounded-2xl bg-sky-50 px-4 py-3 text-sm font-semibold leading-6 text-sky-900 dark:bg-sky-500/10 dark:text-sky-200">
          {point.tagline}
        </p>
      ) : null}

      {/* -------------------------------------------------------------------- */}
      {/* Description enrichie                                                 */}
      {/* -------------------------------------------------------------------- */}

      {point.description ? (
        <div className="mt-4">
          <RichTextPreview
            value={
              point.description
            }
            collapsedHeight={
              DESCRIPTION_COLLAPSED_HEIGHT
            }
          />
        </div>
      ) : (
        <p className="mt-4 text-sm italic leading-7 text-slate-400 dark:text-slate-500">
          Aucune description renseignée.
        </p>
      )}

      {/* -------------------------------------------------------------------- */}
      {/* Adresse                                                              */}
      {/* -------------------------------------------------------------------- */}

      {point.address ? (
        <div className="mt-4 flex items-start gap-2 rounded-2xl bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-600 dark:bg-slate-900 dark:text-slate-300">
          <FiMapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#1277B9]" />

          <span>
            {point.address}
          </span>
        </div>
      ) : null}

      {/* -------------------------------------------------------------------- */}
      {/* Bouton détails                                                       */}
      {/* -------------------------------------------------------------------- */}

      {hasMoreInformation ? (
        <button
          type="button"
          onClick={() =>
            setShowDetails(
              (previous) =>
                !previous,
            )
          }
          className="
            mt-4
            inline-flex
            min-h-11
            w-full
            items-center
            justify-center
            gap-2
            rounded-2xl
            border
            border-slate-200
            bg-slate-50
            px-4
            text-sm
            font-bold
            text-slate-700
            transition
            hover:border-[#1277B9]/30
            hover:bg-sky-50
            hover:text-[#1277B9]

            dark:border-slate-800
            dark:bg-slate-900
            dark:text-slate-200
            dark:hover:border-sky-500/30
            dark:hover:bg-sky-500/10
            dark:hover:text-sky-300
          "
        >
          {showDetails ? (
            <>
              Masquer les informations
              <FiChevronUp className="h-4 w-4" />
            </>
          ) : (
            <>
              Afficher plus
              <FiChevronDown className="h-4 w-4" />
            </>
          )}
        </button>
      ) : null}

      {/* -------------------------------------------------------------------- */}
      {/* Informations complètes                                               */}
      {/* -------------------------------------------------------------------- */}

      {showDetails ? (
        <div className="mt-4 space-y-2">
          {/* Contact */}

          {point.contact ? (
            <DetailRow
              icon={FiUser}
              label="Contact"
            >
              {point.contact}

              {point.contactRole ? (
                <span className="font-normal text-slate-500 dark:text-slate-400">
                  {" "}
                  — {point.contactRole}
                </span>
              ) : null}
            </DetailRow>
          ) : null}

          {/* Fonction seule */}

          {!point.contact &&
          point.contactRole ? (
            <DetailRow
              icon={FiUser}
              label="Fonction"
            >
              {point.contactRole}
            </DetailRow>
          ) : null}

          {/* Téléphone */}

          {point.phone ? (
            <DetailRow
              icon={FiPhone}
              label="Téléphone"
            >
              <a
                href={`tel:${point.phone}`}
                className="transition hover:text-[#1277B9] dark:hover:text-sky-300"
              >
                {point.phone}
              </a>
            </DetailRow>
          ) : null}

          {/* Téléphone secondaire */}

          {point.secondaryPhone ? (
            <DetailRow
              icon={FiPhone}
              label="Téléphone secondaire"
            >
              <a
                href={`tel:${point.secondaryPhone}`}
                className="transition hover:text-[#1277B9] dark:hover:text-sky-300"
              >
                {point.secondaryPhone}
              </a>
            </DetailRow>
          ) : null}

          {/* Emails */}

          {emails.length ? (
            <DetailRow
              icon={FiMail}
              label={
                emails.length > 1
                  ? "Emails"
                  : "Email"
              }
            >
              <div className="space-y-1">
                {emails.map(
                  (email) => (
                    <a
                      key={email}
                      href={`mailto:${email}`}
                      className="block transition hover:text-[#1277B9] dark:hover:text-sky-300"
                    >
                      {email}
                    </a>
                  ),
                )}
              </div>
            </DetailRow>
          ) : null}

          {/* Site web */}

          {point.website ? (
            <DetailRow
              icon={FiGlobe}
              label="Site internet"
            >
              <a
                href={
                  point.website
                }
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[#1277B9] transition hover:underline dark:text-sky-400"
              >
                Visiter le site

                <FiExternalLink className="h-3.5 w-3.5" />
              </a>
            </DetailRow>
          ) : null}

          {/* URL complémentaire */}

          {point.secondaryUrl ? (
            <DetailRow
              icon={
                FiExternalLink
              }
              label="Lien complémentaire"
            >
              <a
                href={
                  point.secondaryUrl
                }
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[#1277B9] transition hover:underline dark:text-sky-400"
              >
                Ouvrir le lien

                <FiExternalLink className="h-3.5 w-3.5" />
              </a>
            </DetailRow>
          ) : null}

          {/* Facebook */}

          {point.facebook ? (
            <DetailRow
              icon={FiFacebook}
              label="Facebook"
            >
              <a
                href={
                  point.facebook
                }
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[#1277B9] transition hover:underline dark:text-sky-400"
              >
                Voir Facebook

                <FiExternalLink className="h-3.5 w-3.5" />
              </a>
            </DetailRow>
          ) : null}

          {/* Instagram */}

          {point.instagram ? (
            <DetailRow
              icon={FiInstagram}
              label="Instagram"
            >
              <a
                href={
                  point.instagram
                }
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[#1277B9] transition hover:underline dark:text-sky-400"
              >
                Voir Instagram

                <FiExternalLink className="h-3.5 w-3.5" />
              </a>
            </DetailRow>
          ) : null}

          {/* Tags */}

          {point.tags ? (
            <DetailRow
              icon={FiTag}
              label="Tags"
            >
              <div className="flex flex-wrap gap-1.5">
                {String(
                  point.tags,
                )
                  .split(",")
                  .map(
                    (tag) =>
                      tag.trim(),
                  )
                  .filter(Boolean)
                  .map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-white px-2.5 py-1 text-xs text-slate-600 shadow-sm dark:bg-slate-950 dark:text-slate-300"
                    >
                      {tag}
                    </span>
                  ))}
              </div>
            </DetailRow>
          ) : null}

          {/* Services */}

          {point.services ? (
            <DetailRow
              icon={FiUsers}
              label="Services"
            >
              {point.services}
            </DetailRow>
          ) : null}

          {/* Accessibilité */}

          {point.accessibility ? (
            <DetailRow
              icon={FiUsers}
              label="Accessibilité"
            >
              {point.accessibility}
            </DetailRow>
          ) : null}

          {/* Paiement */}

          {point.paymentMethods ? (
            <DetailRow
              icon={FiCreditCard}
              label="Moyens de paiement"
            >
              {point.paymentMethods}
            </DetailRow>
          ) : null}

          {/* Horaires */}

          {formattedHours ? (
            <DetailRow
              icon={FiClock}
              label="Horaires"
            >
              <div className="whitespace-pre-line">
                {formattedHours}
              </div>
            </DetailRow>
          ) : null}

          {/* SIRET */}

          {point.siret ? (
            <DetailRow
              icon={FiInfo}
              label="SIRET"
            >
              {point.siret}
            </DetailRow>
          ) : null}

          {/* Informations complémentaires */}

          {point.additionalInfo ? (
            <div className="rounded-2xl bg-slate-50 px-4 py-4 dark:bg-slate-900">
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#1277B9] shadow-sm dark:bg-slate-950 dark:text-sky-400">
                  <FiInfo className="h-4 w-4" />
                </div>

                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">
                  Informations complémentaires
                </p>
              </div>

              <RichTextPreview
                value={
                  point.additionalInfo
                }
                collapsedHeight={
                  150
                }
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {/* -------------------------------------------------------------------- */}
      {/* Google Maps                                                          */}
      {/* -------------------------------------------------------------------- */}

      {point.mapsHref ? (
        <a
          href={
            point.mapsHref
          }
          target="_blank"
          rel="noreferrer"
          className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-4 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0f659d]"
        >
          Ouvrir dans Google Maps

          <FiExternalLink className="h-4 w-4" />
        </a>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                               MiniMap                                      */
/* -------------------------------------------------------------------------- */

function MiniMap({
  points,
  selectedPoint,
  onSelectPoint,
}) {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-100 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <MapContainer
        center={
          selectedPoint
            ? [
                selectedPoint.lat,
                selectedPoint.lng,
              ]
            : DEFAULT_CENTER
        }
        zoom={
          DEFAULT_ZOOM
        }
        className="h-[420px] w-full"
        scrollWheelZoom={
          false
        }
      >
        <ResizeHandler />

        <MapBoundsController
          points={
            points
          }
          selectedPoint={
            selectedPoint
          }
        />

        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {points.map(
          (point) => {
            const type =
              POINT_TYPES[
                point.type
              ] ||
              POINT_TYPES.service;

            const selected =
              selectedPoint?.id ===
              point.id;

            return (
              <CircleMarker
                key={
                  point.id
                }
                center={[
                  point.lat,
                  point.lng,
                ]}
                radius={
                  selected
                    ? 12
                    : 8
                }
                pathOptions={{
                  color:
                    type.stroke,

                  fillColor:
                    type.color,

                  fillOpacity:
                    selected
                      ? 0.96
                      : 0.82,

                  weight:
                    selected
                      ? 4
                      : 2,
                }}
                eventHandlers={{
                  click: () =>
                    onSelectPoint(
                      point,
                    ),
                }}
              >
                <Tooltip
                  direction="top"
                  offset={[
                    0,
                    -4,
                  ]}
                  opacity={
                    0.95
                  }
                >
                  <div className="max-w-[220px] text-xs">
                    <p className="font-bold">
                      {
                        point.title
                      }
                    </p>

                    {point.category ? (
                      <p className="mt-0.5 text-[11px] opacity-80">
                        {
                          point.category
                        }
                      </p>
                    ) : null}

                    <p className="mt-1 text-[11px] font-semibold">
                      {
                        type.singular
                      }
                    </p>
                  </div>
                </Tooltip>
              </CircleMarker>
            );
          },
        )}
      </MapContainer>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                              MapSkeleton                                   */
/* -------------------------------------------------------------------------- */

function MapSkeleton() {
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.75fr)]">
      <div className="h-[420px] animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800" />

      <div className="h-[280px] animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800" />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                         CarteInteretsTeaser                                */
/* -------------------------------------------------------------------------- */

export default function CarteInteretsTeaser() {
  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    allPoints,
    setAllPoints,
  ] = useState([]);

  const [
    selectedPointId,
    setSelectedPointId,
  ] = useState(null);

  const [
    activeTypes,
    setActiveTypes,
  ] = useState({
    association: true,
    commerce: true,
    service: true,
  });

  /* ------------------------------------------------------------------------ */
  /*                              Chargement                                  */
  /* ------------------------------------------------------------------------ */

  const loadPoints =
    async () => {
      setLoading(true);

      try {
        const [
          associations,
          commerces,
          services,
        ] =
          await Promise.all([
            getAssociations().catch(
              () => [],
            ),

            getAllCommerces().catch(
              () => [],
            ),

            getAllServicesMunicipaux().catch(
              () => [],
            ),
          ]);

        /* ------------------------------------------------------------------ */
        /* Associations                                                       */
        /* ------------------------------------------------------------------ */

        const associationPoints =
          ensureArray(
            associations,
          )
            .filter(
              (item) =>
                item?.is_active !==
                false,
            )
            .map(
              normalizeAssociationPoint,
            )
            .filter(
              Boolean,
            );

        /* ------------------------------------------------------------------ */
        /* Commerces                                                          */
        /* ------------------------------------------------------------------ */

        const commercePoints =
          ensureArray(
            commerces,
          )
            .filter(
              (item) =>
                item?.is_active !==
                false,
            )
            .map(
              normalizeCommercePoint,
            )
            .filter(
              Boolean,
            );

        /* ------------------------------------------------------------------ */
        /* Services                                                           */
        /* ------------------------------------------------------------------ */

        const servicePoints =
          ensureArray(
            services,
          )
            .filter(
              (item) =>
                item?.is_active !==
                  false &&
                item?.is_public !==
                  false,
            )
            .map(
              normalizeServicePoint,
            )
            .filter(
              Boolean,
            );

        /* ------------------------------------------------------------------ */
        /* Fusion                                                             */
        /* ------------------------------------------------------------------ */

        const rows =
          sortPoints([
            ...associationPoints,
            ...commercePoints,
            ...servicePoints,
          ]);

        setAllPoints(
          rows,
        );

        setSelectedPointId(
          rows[0]?.id ||
            null,
        );
      } catch (error) {
        console.error(
          "Erreur chargement teaser carte:",
          error,
        );

        setAllPoints(
          [],
        );

        setSelectedPointId(
          null,
        );
      } finally {
        setLoading(
          false,
        );
      }
    };

  /* ------------------------------------------------------------------------ */
  /*                                 Init                                     */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    loadPoints();
  }, []);

  /* ------------------------------------------------------------------------ */
  /*                                 Stats                                    */
  /* ------------------------------------------------------------------------ */

  const stats =
    useMemo(() => {
      return {
        total:
          allPoints.length,

        association:
          allPoints.filter(
            (point) =>
              point.type ===
              "association",
          ).length,

        commerce:
          allPoints.filter(
            (point) =>
              point.type ===
              "commerce",
          ).length,

        service:
          allPoints.filter(
            (point) =>
              point.type ===
              "service",
          ).length,
      };
    }, [allPoints]);

  /* ------------------------------------------------------------------------ */
  /*                           Points visibles                                */
  /* ------------------------------------------------------------------------ */

  const visiblePoints =
    useMemo(() => {
      return allPoints.filter(
        (point) =>
          activeTypes[
            point.type
          ],
      );
    }, [
      allPoints,
      activeTypes,
    ]);

  /* ------------------------------------------------------------------------ */
  /*                           Point sélectionné                              */
  /* ------------------------------------------------------------------------ */

  const selectedPoint =
    useMemo(() => {
      return (
        visiblePoints.find(
          (point) =>
            point.id ===
            selectedPointId,
        ) ||
        visiblePoints[0] ||
        null
      );
    }, [
      visiblePoints,
      selectedPointId,
    ]);

  /* ------------------------------------------------------------------------ */
  /*                Vérification du point après filtre                       */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (
      !visiblePoints.length
    ) {
      setSelectedPointId(
        null,
      );

      return;
    }

    setSelectedPointId(
      (current) => {
        const stillVisible =
          visiblePoints.some(
            (point) =>
              point.id ===
              current,
          );

        return stillVisible
          ? current
          : visiblePoints[0]
              .id;
      },
    );
  }, [visiblePoints]);

  /* ------------------------------------------------------------------------ */
  /*                             Filtres                                      */
  /* ------------------------------------------------------------------------ */

  const toggleType = (
    type,
  ) => {
    setActiveTypes(
      (previous) => ({
        ...previous,

        [type]:
          !previous[type],
      }),
    );
  };

  /* ------------------------------------------------------------------------ */
  /*                             Aucun lieu                                   */
  /* ------------------------------------------------------------------------ */

  if (
    !loading &&
    !allPoints.length
  ) {
    return null;
  }

  /* ------------------------------------------------------------------------ */
  /*                                Render                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <section
      id="carte-interets-teaser"
      className="relative overflow-hidden bg-slate-50 px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20"
      aria-labelledby="carte-teaser-title"
    >
      {/* -------------------------------------------------------------------- */}
      {/* Background                                                           */}
      {/* -------------------------------------------------------------------- */}

      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-emerald-300/15 blur-3xl dark:bg-emerald-500/10" />

        <div className="absolute -right-24 bottom-12 h-80 w-80 rounded-full bg-sky-300/10 blur-3xl dark:bg-sky-500/10" />
      </div>

      <div className="relative mx-auto max-w-[1380px]">
        {/* ------------------------------------------------------------------ */}
        {/* Header                                                             */}
        {/* ------------------------------------------------------------------ */}

        <div className="mb-8 flex flex-col gap-5 md:mb-10 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />

                Carte interactive
              </div>

              <h2
                id="carte-teaser-title"
                className="text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-4xl lg:text-5xl"
              >
                Une carte pour tout retrouver
              </h2>

              <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600 dark:text-slate-300">
                Visualisez rapidement les associations, commerces, artisans et
                services municipaux de la commune sur une carte simplifiée.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <Link
              to="/carte-interets"
              className="group inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-[#1277B9]/40 hover:text-[#1277B9] dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:hover:border-sky-500/40 dark:hover:text-sky-300"
            >
              Voir la carte complète

              <FiArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* Filtres                                                            */}
        {/* ------------------------------------------------------------------ */}

        <Reveal>
          <div className="mb-5 flex flex-col gap-4 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:flex-row sm:items-center sm:justify-between">
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0">
              <TypeStatButton
                type="association"
                active={
                  activeTypes.association
                }
                count={
                  stats.association
                }
                onClick={() =>
                  toggleType(
                    "association",
                  )
                }
              />

              <TypeStatButton
                type="commerce"
                active={
                  activeTypes.commerce
                }
                count={
                  stats.commerce
                }
                onClick={() =>
                  toggleType(
                    "commerce",
                  )
                }
              />

              <TypeStatButton
                type="service"
                active={
                  activeTypes.service
                }
                count={
                  stats.service
                }
                onClick={() =>
                  toggleType(
                    "service",
                  )
                }
              />
            </div>

            <button
              type="button"
              onClick={
                loadPoints
              }
              disabled={
                loading
              }
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900"
            >
              <FiRefreshCw
                className={`h-4 w-4 ${
                  loading
                    ? "animate-spin"
                    : ""
                }`}
              />

              Actualiser
            </button>
          </div>
        </Reveal>

        {/* ------------------------------------------------------------------ */}
        {/* Carte + fiche                                                      */}
        {/* ------------------------------------------------------------------ */}

        {loading ? (
          <MapSkeleton />
        ) : (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.75fr)] lg:items-start">
            <Reveal>
              <MiniMap
                points={
                  visiblePoints
                }
                selectedPoint={
                  selectedPoint
                }
                onSelectPoint={(
                  point,
                ) =>
                  setSelectedPointId(
                    point?.id ||
                      null,
                  )
                }
              />
            </Reveal>

            <Reveal>
              <div className="space-y-5">
                <SelectedPointCard
                  point={
                    selectedPoint
                  }
                />

                {/* ---------------------------------------------------------- */}
                {/* Stats                                                      */}
                {/* ---------------------------------------------------------- */}

                <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Lieux référencés
                  </p>

                  <p className="mt-2 text-3xl font-bold text-slate-950 dark:text-white">
                    {
                      visiblePoints.length
                    }
                  </p>

                  <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                    lieu
                    {visiblePoints.length >
                    1
                      ? "x"
                      : ""}{" "}
                    visible
                    {stats.total
                      ? ` sur ${stats.total}`
                      : ""}
                  </p>

                  <Link
                    to="/carte-interets"
                    className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900"
                  >
                    Rechercher un lieu

                    <FiArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        )}
      </div>
    </section>
  );
}